import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { debounce } from '@mui/material/utils';
import { useCallback, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Sxable } from 'library/types';

import { SearchProductTextField } from './SearchProductTextField';

type SearchFormValues = {
	phrase: string;
};

export type UseSearchParamType = {
	searchParamName?: string;
};

export const useSearchParam = ({
	searchParamName = 'by',
}: UseSearchParamType | undefined = {}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const inputValue = searchParams.get(searchParamName);
	const changeFunc = useCallback(
		(input: string) => {
			setSearchParams((prev) => {
				if (input.length > 0 && prev.get(searchParamName) !== input) {
					prev.set(searchParamName, input);
				}

				return prev;
			});
		},
		[searchParamName, setSearchParams]
	);

	const clearFunc = useCallback(() => {
		setSearchParams((params) => {
			params.set(searchParamName, '');
			return params;
		});
	}, [searchParamName, setSearchParams]);

	return {
		value: inputValue,
		change: changeFunc,
		clear: clearFunc,
	};
};

export interface SearchProductFormParams {
	defaultInput?: string;
	onInputChange: (data: string) => Promise<void> |void;
	onInputClear?: () => void;
	label?: string;
}

export const SearchProductForm = ({
	defaultInput = '',
	onInputChange,
	onInputClear,
	label,
	sx = [],
}: SearchProductFormParams & Sxable) => {
	const { t } = useTranslation(['searchProduct']);

	const { register, handleSubmit, watch, setValue } =
		useForm<SearchFormValues>({
			mode: 'onChange',
			defaultValues: {
				phrase: defaultInput,
			},
		});

	const submitHandler: SubmitHandler<SearchFormValues> = useCallback(
		(data) => {
			if (data.phrase.length > 2) {
				onInputChange(data.phrase);
			}
		},
		[onInputChange]
	);

	const handleClear = () => {
		setValue('phrase', '');
		if (typeof onInputClear === 'function') {
			onInputClear();
		}
	};

	const searchValue = watch('phrase');
	const isSearchValueEmpty = searchValue.length === 0;

	const debouncedSubmit = useMemo(
		() => debounce(handleSubmit(submitHandler), 1500),
		[handleSubmit, submitHandler]
	);

	return (
		<form
			onSubmit={handleSubmit(submitHandler)}
			onReset={handleClear}
			onChange={debouncedSubmit}>
			<SearchProductTextField
				id="search-product-input"
				label={label ?? t('search-input-helper-text')}
				variant="standard"
				//  autofocus here is usability imrpovement
				// eslint-disable-next-line jsx-a11y/no-autofocus
				autoFocus
				slotProps={{
					inputLabel: isSearchValueEmpty
						? { shrink: false }
						: undefined,
					input: {
						startAdornment: (
							<InputAdornment position="start">
								<Search color="primary" />
							</InputAdornment>
						),
						endAdornment: (
							<>
								{!isSearchValueEmpty && (
									<InputAdornment position="end">
										<IconButton type="reset">
											<Close color="primary" />
										</IconButton>
									</InputAdornment>
								)}
							</>
						),
					},
				}}
				{...register('phrase')}
				sx={sx}
			/>
			<input type="submit" hidden />
		</form>
	);
};
